import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import MathCaptcha from "../../Components/UI/MathCaptcha";

const GenerateDeliveryReport = () => {
  const today = new Date().toISOString().split("T")[0];

  const [reportDate, setReportDate] = useState(today);
  const [fileType, setFileType] = useState("pdf");
  const [actionType, setActionType] = useState("I"); // Default = View
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [resetCaptcha, setResetCaptcha] = useState(false);

  // Message system
  const [showMessage, setShowMessage] = useState(false);
  const [messageType, setMessageType] = useState("");
  const [message, setMessage] = useState("");

  const viewerRef = useRef(null);

  // Auto-hide messages
  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setShowMessage(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  // Auto-scroll when PDF loads
  useEffect(() => {
    if (pdfUrl && actionType === "I" && viewerRef.current) {
      setTimeout(() => {
        viewerRef.current.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  }, [pdfUrl, actionType]);

  // Show colored message helper
  const showMessageWithType = (type, msg) => {
    setMessageType(type);
    setMessage(msg);
    setShowMessage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!reportDate) {
      showMessageWithType("error", "Please select a date (कृपया तिथि चुनें)");
      return;
    }

    if (!isVerified) {
      showMessageWithType(
        "error",
        "Please verify the captcha (कृपया कैप्चा सत्यापित करें)"
      );
      return;
    }

    setPdfUrl(null);
    setLoading(true);

    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/export_delivery_report_pdf`;

      if (actionType === "I" && fileType === "pdf") {
        // 👁️ View PDF inline
        const response = await axios.get(apiUrl, {
          params: { date: reportDate, type: fileType, download: "I" },
          responseType: "blob",
        });

        const file = new Blob([response.data], { type: "application/pdf" });
        const url = URL.createObjectURL(file);
        setPdfUrl(url);
        showMessageWithType("info", "Report loaded below 👇");
      } else {
        // 💾 Download file
        const response = await axios.get(apiUrl, {
          params: { date: reportDate, type: fileType, download: "D" },
          responseType: "blob",
        });

        const contentDisposition = response.headers["content-disposition"];
        const filenameMatch = contentDisposition
          ? contentDisposition.match(/filename="?(.+)"?/)
          : null;
        const filename =
          filenameMatch && filenameMatch[1]
            ? filenameMatch[1]
            : `Booking_Report_${reportDate}.${
                fileType === "excel" ? "xlsx" : fileType
              }`;

        const url = URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);

        showMessageWithType("success", "Report downloaded successfully!");
      }
    } catch (error) {
      console.error("Report error:", error);
      if (error.response?.status === 404) {
        showMessageWithType("error", "No data found for the selected date.");
      } else {
        showMessageWithType(
          "error",
          "Failed to process request. Try again later."
        );
      }
    } finally {
      setResetCaptcha(true);
      setIsVerified(false);

      // Reset loading state
      setTimeout(() => setLoading(false), 400);

      // Important 👇 toggle resetCaptcha back after a brief delay
      setTimeout(() => setResetCaptcha(false), 100);
    }
  };

  // 🎨 Unified input style
  const inputStyle = {
    width: "100%",
    height: "40px",
    fontSize: "15px",
    padding: "6px 10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  // Message color based on type
  const messageColor =
    messageType === "success"
      ? "green"
      : messageType === "error"
      ? "red"
      : "blue";

  return (
    <div className="container" style={{ maxWidth: "600px", marginTop: "0px" }}>
      <div className="admin-wrap">
        <div className="box-head">
          <span>Delivery Report : जल डिलीवरी रिपोर्ट</span>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Date Picker */}
          <div className="form-group" style={{ marginBottom: "15px" }}>
            <label style={{ fontWeight: "bold" }}>
              For Booking Date (तिथि चुनें)
            </label>
            <input
              type="date"
              required
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Format Dropdown */}
          <div className="form-group" style={{ marginBottom: "15px" }}>
            <label style={{ fontWeight: "bold" }}>
              Select Format (फॉर्मेट चुनें)
            </label>
            <select
              value={fileType}
              onChange={(e) => setFileType(e.target.value)}
              style={inputStyle}
            >
              <option value="pdf">PDF</option>
              <option value="excel">Excel (.xlsx)</option>
              <option value="csv">CSV</option>
            </select>
          </div>

          {/* Action Dropdown */}
          <div className="form-group" style={{ marginBottom: "15px" }}>
            <label style={{ fontWeight: "bold" }}>Action (क्रिया चुनें)</label>
            <select
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
              style={inputStyle}
            >
              <option value="I">View (देखें)</option>
              <option value="D">Download (डाउनलोड करें)</option>
            </select>
          </div>

          {/* Math Captcha */}
          <div
            className="form-group"
            style={{
              display: "block",
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            <MathCaptcha onVerification={setIsVerified} reset={resetCaptcha} />
          </div>

          {/* Submit Button */}
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <button
              type="submit"
              disabled={loading}
              className="readMoreButton"
              style={{
                minWidth: "160px",
                height: "42px",
                fontSize: "16px",
                borderRadius: "6px",
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? (
                <>
                  <span
                    className="spinner"
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid #fff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      display: "inline-block",
                      marginRight: "8px",
                      animation: "spin 0.8s linear infinite",
                    }}
                  ></span>
                  Processing...
                </>
              ) : actionType === "I" ? (
                "Generate"
              ) : (
                "Download"
              )}
            </button>
          </div>
        </form>

        {/* Message */}
        {showMessage && (
          <p
            style={{
              textAlign: "center",
              marginTop: "15px",
              color: messageColor,
              fontSize: "15px",
              fontWeight: "500",
            }}
          >
            {message}
          </p>
        )}
      </div>

      {/* Inline PDF Viewer */}
      {pdfUrl && (
        <div ref={viewerRef} style={{ marginTop: "25px" }}>
          <iframe
            src={pdfUrl}
            title="Report Preview"
            width="100%"
            height="600px"
            style={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              boxShadow: "0 0 8px rgba(0,0,0,0.1)",
            }}
          ></iframe>
        </div>
      )}

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GenerateDeliveryReport;
